import type { components } from "../../types/api";
import { API_BASE, apiFetch, toQuery } from "../../lib/api";

export type MembershipList = components["schemas"]["MembershipList"];
export type MembershipRow = components["schemas"]["MembershipRow"];
export type MembershipStats = components["schemas"]["MembershipStats"];
export type MembershipKpis = components["schemas"]["MembershipKpis"];
export type MembershipExpiryCalendar = components["schemas"]["MembershipExpiryCalendar"];
export type MembershipExpiryDay = components["schemas"]["MembershipExpiryDay"];

export type MembershipStatus = MembershipRow["status"]; // "active" | "expired" | "upcoming"
export type MembershipStatusFilter = MembershipStatus | "all";

/**
 * The taxonomy buckets that are real memberships — the roster's own scope
 * (services/memberships.ROSTER_ACTIVE_BUCKETS), which is why filtering to any
 * one of them never empties the list the way a non-membership bucket
 * (trainer, day_pass, comp) would.
 *
 * This is the length axis: how long a term was sold for. `visit_pack` and `gym`
 * ride along because they are what the roster shows instead of a month count —
 * a punch card is sold by visits and gym-floor access by what it opens, not by
 * a term — and leaving them out would make those rows unfilterable.
 *
 * Order follows BUCKET_ORDER in services/membership_taxonomy.py.
 */
export const MEMBERSHIP_LENGTHS = ["visit_pack", "1m", "3m", "6m", "12m", "gym"] as const;
export type MembershipLength = (typeof MEMBERSHIP_LENGTHS)[number];
export type MembershipSort = "expiry" | "start" | "client" | "type";
export type SortDir = "asc" | "desc";

export type MembershipsSearchParams = {
  q?: string;
  status?: MembershipStatusFilter;
  service_name?: string;
  exclude_service_name?: string[];
  // POS sales category — the deal a term was rung under (e.g. "TBC WM", "SOLO").
  category?: string;
  exclude_category?: string[];
  // Term length (taxonomy bucket). Under the default latest-only roster this
  // asks "whose CURRENT term is this length" — the collapse to one row per
  // member happens before the filter, so a member on a year plan who also holds
  // an old monthly one is not pulled back by picking 1m. `show_all_terms`
  // widens it to every term they hold.
  bucket?: MembershipLength;
  include_single_visit?: boolean;
  expiring_within_days?: number;
  expiry_from?: string; // YYYY-MM-DD
  expiry_to?: string; // YYYY-MM-DD
  // Opt in to every term a member holds. Off = the API default (latest_only), one
  // row per member. Inverted so the common case stays out of the URL.
  show_all_terms?: boolean;
  sort?: MembershipSort;
  sort_dir?: SortDir;
  page?: number;
  page_size?: number;
};

export function membershipsToParams(s: MembershipsSearchParams): URLSearchParams {
  return toQuery({
    q: s.q,
    status: s.status,
    service_name: s.service_name,
    exclude_service_name: s.exclude_service_name,
    category: s.category,
    exclude_category: s.exclude_category,
    bucket: s.bucket,
    include_single_visit: s.include_single_visit,
    expiring_within_days: s.expiring_within_days,
    expiry_from: s.expiry_from,
    expiry_to: s.expiry_to,
    // A string, not a boolean: toQuery drops `false` (it assumes opt-in flags).
    latest_only: s.show_all_terms ? "false" : undefined,
    sort: s.sort,
    sort_dir: s.sort_dir,
    page: s.page ?? 1,
    page_size: s.page_size ?? 50,
  });
}

export const getMemberships = (s: MembershipsSearchParams) =>
  apiFetch<MembershipList>(`/api/memberships?${membershipsToParams(s)}`);

export const getMembershipServiceNames = () => apiFetch<string[]>("/api/memberships/service-names");

export const getMembershipCategories = () => apiFetch<string[]>("/api/memberships/categories");

export const getMembershipStats = (s: MembershipsSearchParams) =>
  apiFetch<MembershipStats>(`/api/memberships/stats?${membershipsToParams(s)}`);

/**
 * Per-day expiry counts for the strip above the roster.
 *
 * Sends the roster's SCOPE filters (what counts as in view) but deliberately not
 * its expiry filters — those pick a day out of this calendar, so passing them
 * back would flatten every other day to zero.
 */
export function membershipExpiryCalendarToParams(
  s: MembershipsSearchParams,
  range: { date_from: string; date_to: string },
): URLSearchParams {
  return toQuery({
    date_from: range.date_from,
    date_to: range.date_to,
    q: s.q,
    // `status` is deliberately absent as well: it is relative to today, so
    // sending "active" would empty every day before it.
    service_name: s.service_name,
    exclude_service_name: s.exclude_service_name,
    category: s.category,
    exclude_category: s.exclude_category,
    bucket: s.bucket,
    include_single_visit: s.include_single_visit,
    latest_only: s.show_all_terms ? "false" : undefined,
  });
}

export const getMembershipExpiryCalendar = (
  s: MembershipsSearchParams,
  range: { date_from: string; date_to: string },
) =>
  apiFetch<MembershipExpiryCalendar>(
    `/api/memberships/expiry-calendar?${membershipExpiryCalendarToParams(s, range)}`,
  );

/** Download URL for the per-day totals across the strip's window. */
export const expiryCountsXlsxUrl = (
  s: MembershipsSearchParams,
  range: { date_from: string; date_to: string },
) =>
  `${API_BASE}/api/memberships/expiry-calendar?${membershipExpiryCalendarToParams(s, range)}&format=xlsx`;

/**
 * Download URL for the memberships themselves across the strip's window.
 *
 * Pins the roster params to the window and asks for every status: the window
 * spans both sides of today, and the roster's default "active" would drop the
 * already-expired half — the export would then silently disagree with the
 * totals shown right above it.
 */
export const expiryRowsXlsxUrl = (
  s: MembershipsSearchParams,
  range: { date_from: string; date_to: string },
) =>
  `${API_BASE}/api/memberships?${toQuery({
    q: s.q,
    status: "all",
    service_name: s.service_name,
    exclude_service_name: s.exclude_service_name,
    category: s.category,
    exclude_category: s.exclude_category,
    bucket: s.bucket,
    include_single_visit: s.include_single_visit,
    latest_only: s.show_all_terms ? "false" : undefined,
    expiry_from: range.date_from,
    expiry_to: range.date_to,
    sort: "expiry",
    format: "xlsx",
  })}`;

export type MembershipKpisParams = {
  date_from: string; // YYYY-MM-DD
  date_to: string;
  prev_from: string;
  prev_to: string;
  include_single_visit?: boolean;
  exclude_service_name?: string[];
};

export function membershipKpisToParams(p: MembershipKpisParams): URLSearchParams {
  return toQuery({
    date_from: p.date_from,
    date_to: p.date_to,
    prev_from: p.prev_from,
    prev_to: p.prev_to,
    include_single_visit: p.include_single_visit,
    exclude_service_name: p.exclude_service_name,
  });
}

export const getMembershipKpis = (p: MembershipKpisParams) =>
  apiFetch<MembershipKpis>(`/api/memberships/kpis?${membershipKpisToParams(p)}`);
